import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getDisplayName } from "../utils/metalNameFixer";
import clsx from "clsx";

/* ======================================================
   🔑 NORMALIZER (same as backend logic)
   ====================================================== */
const normalize = (s) => {
  if (!s) return "";
  return String(s)
    .replace(/\u2013|\u2014/g, "-")
    .replace(/[()\/.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

/* ======================================================
   ✅ CHECK: metal ever had a real rate
   ====================================================== */
const hasAnyRate = (r) => {
  const buy = Number(r.buy);
  const sell = Number(r.sell);
  return Number.isFinite(buy) || Number.isFinite(sell);
};

/* ======================================================
   ✅ DEDUPE metals (prefer one with rate)
   ====================================================== */
const dedupeByName = (rates) => {
  const map = new Map();
  rates.forEach((r) => {
    const key = normalize(r.name);
    if (!map.has(key) || hasAnyRate(r)) {
      map.set(key, r);
    }
  });
  return Array.from(map.values());
};

/* ======================================================
   📌 DESIRED DISPLAY ORDER
   ====================================================== */
const desiredOrder = [
  "GOLD 999",
  "GOLD 999 (any)",
  "GOLD 999 RTGS",
  "GOLD 99.50",
  "GOLD 99 50 RTGS",
  "GOLD ANY RTGS 995",
  "SILVER CHORSA 99.00",
  "SILVER PETI",
  "SILVER (PETTY)",
  "SILVER PETI RTGS",
].map(normalize);

/* ======================================================
   🏷️ DISPLAY NAME OVERRIDES (UI ONLY)
   ====================================================== */
const displayNameMap = {
  [normalize("GOLD 999 (any)")]: "GOLD 999",
  [normalize("GOLD 999 RTGS")]: "GOLD 999 RTGS",
  [normalize("GOLD 99.50")]: "GOLD 99.50",
  [normalize("GOLD 99 50 RTGS")]: "GOLD 99.50 RTGS",
  [normalize("SILVER CHORSA 99.00")]: "SILVER CHORSA 99.00",
  [normalize("SILVER (PETTY)")]: "SILVER PETI",
  [normalize("SILVER PETI")]: "SILVER PETI",
  [normalize("SILVER PETI RTGS")]: "SILVER PETI RTGS",
  [normalize("GOLD ANY RTGS 995")]: "GOLD 99.50 RTGS",

};

/* ======================================================
   🧩 COMPONENT
   ====================================================== */
const CustomRatesTable = ({ rates }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [prevRates, setPrevRates] = useState({});
  const [flashes, setFlashes] = useState({});

  /* ======================================================
     🔄 SORT + CLEAN PIPELINE
     ====================================================== */
  const finalRates = useMemo(() => {
    if (!Array.isArray(rates)) return [];

    // 1️⃣ dedupe
    const unique = dedupeByName(rates);

    // 2️⃣ remove blank / never-traded metals
    const filtered = unique.filter(hasAnyRate);

    // 3️⃣ stable sort
    return filtered
      .map((r, idx) => ({
        r,
        norm: normalize(r.name),
        idx,
      }))
      .sort((a, b) => {
        const ia = desiredOrder.indexOf(a.norm);
        const ib = desiredOrder.indexOf(b.norm);

        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;

        const nameA = (displayNameMap[a.norm] || a.r.name).toLowerCase();
        const nameB = (displayNameMap[b.norm] || b.r.name).toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        return a.idx - b.idx;
      })
      .map((x) => x.r);
  }, [rates]);

  /* ======================================================
     ✨ FLASH LOGIC
     ====================================================== */
  useEffect(() => {
    const newFlashes = {};

    finalRates.forEach((rate) => {
      const prev = prevRates[rate.name];
      if (!prev) return;

      ["buy", "sell"].forEach((field) => {
        const prevVal = Number(prev[field]);
        const currVal = Number(rate[field]);
        if (Number.isFinite(prevVal) && Number.isFinite(currVal) && prevVal !== currVal) {
          newFlashes[`${rate.name}-${field}`] =
            currVal > prevVal ? "up" : "down";
        }
      });
    });

    if (Object.keys(newFlashes).length) {
      setFlashes(newFlashes);
      setTimeout(() => setFlashes({}), 700);
    }

    const map = {};
    finalRates.forEach((r) => {
      map[r.name] = { buy: r.buy, sell: r.sell };
    });
    setPrevRates(map);
  }, [finalRates]);

  /* ======================================================
     🔗 NAVIGATION
     ====================================================== */
const handleNavigate = (rate, action) => {
  if (!user) return navigate("/login");
  navigate(`/trade/${rate.key}/${action}`);
};


  const getDisplayFor = (name) => {
    const norm = normalize(name);
    return displayNameMap[norm] || getDisplayName(name) || name;
  };

  /* ======================================================
     🧾 RENDER
     ====================================================== */
  return (
    <div className="border rounded-xl shadow-md p-4 bg-black text-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-yellow-400 border-b border-yellow-600">
            <th className="py-2">Name</th>
            <th className="py-2">Buy</th>
            <th className="py-2">Sell</th>
          </tr>
        </thead>
        <tbody>
          {finalRates.map((r) => (
            <tr
              key={normalize(r.name)}
              className="text-center border-t border-gray-700"
            >
              <td className="py-2">{getDisplayFor(r.name)}</td>

              <td
                onClick={() => handleNavigate(r, "buy")}
                className={clsx(
                  "py-2 font-semibold cursor-pointer transition",
                  flashes[`${r.name}-buy`] === "up" && "text-green-400 animate-pulse",
                  flashes[`${r.name}-buy`] === "down" && "text-red-400 animate-pulse"
                )}
              >
                {r.buy ?? "-"}
              </td>

              <td
                onClick={() => handleNavigate(r, "sell")}
                className={clsx(
                  "py-2 font-semibold cursor-pointer transition",
                  flashes[`${r.name}-sell`] === "up" && "text-green-400 animate-pulse",
                  flashes[`${r.name}-sell`] === "down" && "text-red-400 animate-pulse"
                )}
              >
                {r.sell ?? "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CustomRatesTable;
